import React, { useEffect } from 'react';

import { isEmpty, isNil } from 'lodash';

import { useDispatch } from 'react-redux';

import { tryRefreshingToken } from 'actions/user';

import Api from 'core/api';
import useMediaInfo from 'hooks/useMediaInfo';
import useMutation from 'hooks/useMutation';
import useRedirect from 'hooks/useRedirect';
import useUserRedux from 'hooks/useUserRedux';
import { ensureObject } from 'utils/objects';

import WithPopover from 'modules/AccountSetupV2/components/WithPopover';
import {
  DASHBOARD_MODES,
  ALLOWED_NAVIGATE_ROUTES,
} from 'modules/AccountSetupV2/widgets/K12DashboardSwitcher/constants';
import SwitcherRedirectMap from 'modules/AccountSetupV2/widgets/K12DashboardSwitcher/switcherRedirectMap';
import Switcher from 'modules/AccountSetupV2/widgets/UserModeSwitcher/Switcher';
import SwitcherDropdown from 'modules/AccountSetupV2/widgets/UserModeSwitcher/SwitcherDropdown';
import {
  resetFilters,
  setHasFetchedFilters,
} from 'modules/StudentScopeFilter/actions';

const K12DashboardSwitcher = () => {
  const dispatch = useDispatch();
  const { redirect } = useRedirect();
  const { isMobile } = useMediaInfo();
  const {
    data: { currentUserMode, userModes },
  } = useUserRedux();

  const { mutateAsync: updateCurrentUserMode } = useMutation({
    apiFunc: Api.slUsers.updateCurrentUserMode,
    onSuccess: async ({ data }) => {
      const { currentUserMode: newMode } = ensureObject(data);
      await dispatch(tryRefreshingToken());
      window.location.href = DASHBOARD_MODES[newMode]?.route;
    },
  });

  useEffect(() => {
    const { pathname } = window.location;
    const allowNavigate = ALLOWED_NAVIGATE_ROUTES.includes(pathname);

    if (
      allowNavigate &&
      currentUserMode &&
      pathname !== DASHBOARD_MODES[currentUserMode].route
    ) {
      SwitcherRedirectMap[currentUserMode]({ redirect });
    }
  }, [currentUserMode]);

  if (isNil(userModes) || isEmpty(userModes) || userModes?.length <= 1) {
    return null;
  }

  const getActiveDashboardModes = () => {
    const modeKeys = userModes.map(({ mode }) => [mode, DASHBOARD_MODES[mode]]);
    return Object.fromEntries(modeKeys);
  };

  return (
    <WithPopover
      messagePlacement="bottom"
      enabledMessage="Switch your currently active dashboard on SchooLinks"
      popover={({ setAnchor }) => (
        <SwitcherDropdown
          setOption={selected => {
            dispatch(resetFilters());
            dispatch(setHasFetchedFilters(false));
            updateCurrentUserMode({
              payload: { currentUserMode: selected },
            });
          }}
          isMobile={isMobile}
          setAnchor={setAnchor}
          options={getActiveDashboardModes()}
          selectedOption={currentUserMode}
        />
      )}
      render={({ setAnchor, anchor }) => (
        <Switcher
          isMobile={isMobile}
          setAnchor={setAnchor}
          anchor={anchor}
          options={getActiveDashboardModes()}
          selectedOption={currentUserMode}
        />
      )}
    />
  );
};

export default K12DashboardSwitcher;
